import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LoadingScreen } from "@/components/ProtectedRoute";
import { NovaLogo } from "@/components/landing/Logo";
import { ROLE_LABELS } from "@/lib/roles";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"/>
    <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.6 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z"/>
  </svg>
);

export default function ActivatePage() {
  const { token } = useParams();
  const { setUser, API } = useAuth();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/invitations/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (active) setInvitation(data);
      })
      .catch((err) => {
        if (active) setError(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [API, token]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/activate/${encodeURIComponent(token)}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const activateWithPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/activate`,
        { token, password },
        { withCredentials: true }
      );
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen label="Checking your invitation…" />;

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="activate-page">
      <div className="hidden lg:flex relative flex-col justify-between bg-nova-navy text-white p-12 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-nova-blue/20 blur-3xl" />
        <div className="absolute inset-0 bg-grid-navy opacity-[0.15]" />
        <div className="relative">
          <NovaLogo inverted />
        </div>
        <div className="relative">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl xl:text-5xl font-extrabold tracking-tighter leading-[1.05]"
          >
            Your workspace
            <br />
            <span className="text-blue-400">is waiting.</span>
          </motion.h2>
          <div className="mt-10 flex items-start gap-3 text-slate-300 max-w-md">
            <span className="w-7 h-7 rounded-full bg-nova-blue/25 flex items-center justify-center shrink-0 mt-0.5">
              <ShieldCheck size={14} className="text-blue-300" />
            </span>
            <p className="text-sm leading-relaxed">
              This invitation is locked to the approved email address, organization, and role. It expires automatically after seven days.
            </p>
          </div>
        </div>
        <p className="relative text-[10px] font-semibold tracking-[0.32em] text-blue-400">
          A NEW WAY FORWARD.
        </p>
      </div>

      <div className="flex items-center justify-center px-5 sm:px-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <Link
            to="/"
            data-testid="activate-back-home-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-nova-muted hover:text-nova-navy transition-colors mb-8"
          >
            <ArrowLeft size={15} />
            Back to site
          </Link>

          {!invitation ? (
            <div className="rounded-3xl border border-red-100 bg-red-50 p-8" data-testid="activate-invalid-message">
              <XCircle size={34} className="text-red-500" />
              <h1 className="mt-5 text-3xl font-bold tracking-tighter text-nova-navy">Invitation unavailable</h1>
              <p className="mt-3 text-sm text-red-700 leading-relaxed">{error}</p>
              <Link
                to="/request-demo"
                data-testid="activate-request-demo-link"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-nova-blue px-6 py-3 text-sm font-semibold text-white hover:bg-nova-bluedark transition-colors duration-300"
              >
                Request a demo
                <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 rounded-full bg-nova-mist border border-nova-blue/15 px-4 py-1.5 mb-6">
                <Check size={14} className="text-nova-blue" strokeWidth={2.4} />
                <span className="text-xs font-semibold text-nova-blue" data-testid="activate-valid-badge">
                  Secure invitation verified
                </span>
              </div>
              <h1 className="text-3xl font-bold tracking-tighter text-nova-navy">Activate your account</h1>
              <p className="mt-2 text-sm text-nova-slate leading-relaxed">
                Join <span className="font-semibold text-nova-navy" data-testid="activate-org-name">{invitation.org_name}</span> as{" "}
                <span className="font-semibold text-nova-navy" data-testid="activate-role">
                  {ROLE_LABELS[invitation.role] || "Team Member"}
                </span>.
              </p>
              <p className="mt-3 rounded-2xl bg-nova-mist px-4 py-3 text-sm font-medium text-nova-navy" data-testid="activate-email">
                {invitation.email}
              </p>

              {invitation.has_account ? (
                <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6" data-testid="activate-existing-account">
                  <p className="text-sm text-nova-slate leading-relaxed">
                    This email already has a Nova Opsis account. Sign in to continue.
                  </p>
                  <Link
                    to="/login"
                    data-testid="activate-signin-link"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-nova-blue px-6 py-3 text-sm font-semibold text-white hover:bg-nova-bluedark transition-colors duration-300"
                  >
                    Sign in
                    <ArrowRight size={15} />
                  </Link>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleGoogle}
                    data-testid="activate-google-btn"
                    className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-nova-navy hover:border-nova-blue/40 hover:bg-nova-mist transition-colors duration-300"
                  >
                    <GoogleIcon />
                    Continue with Google
                  </button>

                  <div className="my-6 flex items-center gap-4">
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className="text-xs font-medium text-nova-muted">or set a password</span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </div>

                  <form onSubmit={activateWithPassword} className="space-y-4" data-testid="activate-form">
                    <div>
                      <label htmlFor="activate-password" className="block text-xs font-semibold text-nova-navy mb-1.5">
                        Password <span className="text-nova-muted font-normal">(8+ characters)</span>
                      </label>
                      <input
                        id="activate-password"
                        data-testid="activate-password-input"
                        type="password"
                        required
                        minLength={8}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                      />
                    </div>
                    <div>
                      <label htmlFor="activate-password-confirm" className="block text-xs font-semibold text-nova-navy mb-1.5">
                        Confirm password
                      </label>
                      <input
                        id="activate-password-confirm"
                        data-testid="activate-password-confirm-input"
                        type="password"
                        required
                        minLength={8}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                      />
                    </div>

                    {error && (
                      <p data-testid="activate-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                        {error}
                      </p>
                    )}

                    <motion.button
                      type="submit"
                      disabled={submitting}
                      data-testid="activate-submit-btn"
                      whileHover={{ scale: submitting ? 1 : 1.02 }}
                      whileTap={{ scale: submitting ? 1 : 0.98 }}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nova-blue px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300 disabled:opacity-60"
                    >
                      {submitting && <Loader2 size={16} className="animate-spin" />}
                      Activate account
                      {!submitting && <ArrowRight size={15} />}
                    </motion.button>
                  </form>
                </>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
